import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, getFilterDates, inDatepickerToTimestamp, isIntercambio, ReportService, Route } from '@philips-emr/hpms-stipulator-commons';
import StipulatorContext, { Entry, IeTipoEstipulante } from '@philips-emr/hpms-stipulator-commons/lib/contexts/stipulator-context';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components';
import { HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { ActionAPI, AutocompleteAPI, FetchAPI, FilterAPI, Token } from '@philips-emr/tws-components';
import { Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnClick, OnDynamicContentRender, OnMount, OnSubmit, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { applicationContext } from '@philips-emr/tws-foundation';

import { AlteracaoBeneficiarioAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';
import { getLoteStampBySituacao, LoteSituacao } from '../lote/lote-utils';
import './alteracao-beneficiario.css';

enum SituacaoAlteracao {
  INATIVO = 'I',
  ATIVO = 'A',
  ADESAO = 'D',
  MIGRACAO = 'M'
}

interface Data {
  dsSituacao: string;
  dsSituacaoTrabalhista: string,
  ieSituacao: SituacaoAlteracao;
  nrSeqSegurado: number,
  cdPessoaFisica: number,
  nrSeqTitular: number,
  nrSequenciaRepasse: number,
  tipoContrato: string,
  qtdDependentes: number,
}

interface DefaultFilterValues {
  vlDominioData: object;
  cdSituacao: object;
  vlDominio: object;
  dtInicio: string; 
  dtFim: string;
  nrSeqSegurado: string;
  nrSeqSubestipulante: string;
}

interface Report {
  cdClassifRelat: string;
  cdRelatorio: string;
  dsTitulo: string;
  nrSequencia: string;
  cdGrupo: string;
}

interface SituacaoTrabalhista {
  cdSituacao: string,
  vlSituacao: string,
}

interface TipoData {
  dsDominioData: string,
  vlDominioData: string,
}

interface SituacaoBeneficiario {
  dsDominio: string,
  vlDominio: string,
}

interface Segurado {
  dsTitularidade: string,
  nmPessoaFisica: string,
  nrSeqSegurado: number,
}

interface Subestipulante {
  dsSubEstipulante: string,
  dsLabelCD: string,
  cdSubestipulante: string,
  nrSeqSubestipulante: number,
}

const OptionKey = {
  DADOS_PESSOAIS: 'dadosPessoais',
  DATA_ADISSAO: 'dataAdmissao',
  LOCALIZACAO: 'localizacao',
  MATRICULA: 'matricula',
  PRODUTO: 'produto',
  VINCULO: 'vinculo',
  COMPARTILHAMENTO_RISCO_FIM: 'compatilhamentoRiscoFinalizar',
  MIGRAR: 'migrar',
  COMPARTILHAMENTO_RISCO_SOLIC: 'compatilhamentoRiscoSolicitar',
  CARTEIRA: 'carteira',
  TITULARIDADE: 'titularidade',
  SUBESTIPULANTE: 'subestipulante',

};

@Controller({ fragment: FragmentNames.ALTERACAO_BENEFICIARIO })
class AlteracaoBeneficiarioController extends BaseController {

  @Inject(Aliases.FETCH_BENEFICIARY_CHANGE)
  private fetchAPI: FetchAPI;
  @Inject(Aliases.FETCH_SUB_ESTIPULANTE)
  private fetchSubestipulante: FetchAPI;
  @Inject(Aliases.ACTION_PRINT)
  private print: ActionAPI;
  @Inject(Aliases.FILTER_BENEFICIARY_CHANGE)
  private filter: FilterAPI;

  private reportService: ReportService;

  @OnMount(Aliases.FILTERED_LIST)
  onMountFilteredList(): void {
    applicationContext.openModule('alteracao-beneficiario');
  }

  @OnMount(Aliases.ACTION_PRINT)
  onMountAction(): void {

    this.reportService = new ReportService('15', Endpoint.GET.RELATORIOS_POR_GRUPO);
    void this.reportService.getRelatoriosByCdGrupo()
      .then((response: Report[]) => {
        if (response.length === 0) {
          this.print.setEnabled(false);
        }
      });
  }

  @OnMount(Aliases.AUTOCOMPLETE_SUB_ESTIPULANTE)
  onAutocompleteMount(event: MouseEvent): void {
    const api = event.target as unknown as AutocompleteAPI;

    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    nrSeqSubestipulante && this.fetchSubestipulante.setParameters({ ...this.fetchSubestipulante.getParameters(), nrSeqSubestipulante });

    if (isIntercambio() || nrSeqSubestipulante) {
      api.setEnabled(false);
    }
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_SITUACAO)
  onDynamicContentRenderFieldSituacao(event: RenderEvent): void {
    const { data, render } = event;
    const { ieSituacao } = data as Data;

    const ieSituacaoMap = {
      [SituacaoAlteracao.INATIVO]: LoteSituacao.REJEITADA_PELA_OPERADORA,
      [SituacaoAlteracao.ATIVO]: LoteSituacao.INTEGRADO_A_PROPOSTA,
      [SituacaoAlteracao.ADESAO]: LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO,
      [SituacaoAlteracao.MIGRACAO]: LoteSituacao.PENDENTE,
    };

    const dsSituacaoMap = {
      [SituacaoAlteracao.INATIVO]: MessageService.translate('$expression.$319239'),
      [SituacaoAlteracao.ATIVO]: MessageService.translate('$expression.$894905'),
      [SituacaoAlteracao.ADESAO]: MessageService.translate('$expression.$330512'),
      [SituacaoAlteracao.MIGRACAO]: MessageService.translate('$expression.$331187'),
    };

    const content = (): ReactElement => (getLoteStampBySituacao(ieSituacaoMap[ieSituacao], dsSituacaoMap[ieSituacao]));

    render(content);
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD)
  onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { dsSituacaoTrabalhista } = data as Data;

    if (dsSituacaoTrabalhista) {
      render(() => (
        <div className="u-token-solicitacoes-pendentes">
          <Token
            value={dsSituacaoTrabalhista}
            type={Type.NEUTRAL} />
        </div>)
      );
    }
  }

  @HPMSOnCreateOption(Aliases.HPMS_CUSTOM_CARD)
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data: dadosContextMenu } = event;

    const { dtRescisao, ieSituacao } = dadosContextMenu;

    const showOtherOptions = dtRescisao === undefined
      && ieSituacao !== 'I'
      && StipulatorContext.getCurrentContract().ieTipoEstipulante === IeTipoEstipulante.PJ;

    const { key, shouldRender } = event;

    const show = {
      [OptionKey.DADOS_PESSOAIS]: true,
      [OptionKey.DATA_ADISSAO]: showOtherOptions ? this.renderDataAdmissao(dadosContextMenu as unknown as Data) : false,
      [OptionKey.LOCALIZACAO]: showOtherOptions ? this.renderLocalizacao() : false,
      [OptionKey.MATRICULA]: showOtherOptions ? this.renderMatricula() : false,
      [OptionKey.PRODUTO]: showOtherOptions ? this.renderProduto(dadosContextMenu as unknown as Data) : false,
      [OptionKey.VINCULO]: showOtherOptions ? this.renderVinculo() : false,
      [OptionKey.COMPARTILHAMENTO_RISCO_FIM]: showOtherOptions ? this.renderCompartilhamentoRiscoFim(dadosContextMenu as unknown as Data) : false,
      [OptionKey.MIGRAR]: showOtherOptions ? this.renderMigrarContrato(dadosContextMenu as unknown as Data) : false,
      [OptionKey.COMPARTILHAMENTO_RISCO_SOLIC]: showOtherOptions ? this.renderCompartilhamentoRiscoSolic(dadosContextMenu as unknown as Data) : false,
      [OptionKey.CARTEIRA]: showOtherOptions ? this.renderSegundavia() : false,
      [OptionKey.TITULARIDADE]: showOtherOptions ? this.renderAlterarTitularidade(dadosContextMenu as unknown as Data) : false,
      [OptionKey.SUBESTIPULANTE]: showOtherOptions ? this.renderAlterarSubestipulante(dadosContextMenu as unknown as Data) : false,

    };
    shouldRender(show[key]);

  }

  @HPMSOnOptionClick(Aliases.HPMS_CUSTOM_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqSegurado: nrSeqBeneficiario, cdPessoaFisica } = data as unknown as Data;

    const action = {
      [OptionKey.DADOS_PESSOAIS]: () => this.onDadosPessoais(nrSeqBeneficiario, cdPessoaFisica),
      [OptionKey.DATA_ADISSAO]: () => this.onAlterarDataAdmissao(nrSeqBeneficiario),
      [OptionKey.LOCALIZACAO]: () => this.onAlterarLocalizacao(nrSeqBeneficiario),
      [OptionKey.MATRICULA]: () => this.onAlterarMatricula(nrSeqBeneficiario),
      [OptionKey.PRODUTO]: () => this.onAlterarProduto(nrSeqBeneficiario, cdPessoaFisica),
      [OptionKey.VINCULO]: () => this.onAlterarVinculo(nrSeqBeneficiario),
      [OptionKey.COMPARTILHAMENTO_RISCO_FIM]: () => this.onFinalizarRepasse(nrSeqBeneficiario),
      [OptionKey.MIGRAR]: () => this.onMigrarContrato(nrSeqBeneficiario),
      [OptionKey.COMPARTILHAMENTO_RISCO_SOLIC]: () => this.onSolicitarRepasse(nrSeqBeneficiario),
      [OptionKey.CARTEIRA]: () => this.onSegundaViaCartao(nrSeqBeneficiario),
      [OptionKey.TITULARIDADE]: () => this.onTransferirTitularidade(nrSeqBeneficiario),
      [OptionKey.SUBESTIPULANTE]: () => this.onAlterarSubestipulante(nrSeqBeneficiario),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnSubmit(Aliases.FILTER_BENEFICIARY_CHANGE)
  onSubmitFilter(event: SubmitEvent): void {
    const { record, target } = event;
    const api = target as unknown as FilterAPI;

    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    if (nrSeqSubestipulante) {
      nrSeqSubestipulante && this.fetchAPI.setParameters({ ...this.fetchAPI.getParameters(), nrSeqSubestipulante });

      if (record && record.dtInicio && record.dtFim) {
        return;
      }

      const { dtInicio, dtFim } = getFilterDates();
      !record.dtInicio && (record.dtInicio = dtInicio);
      !record.dtFim && (record.dtFim = dtFim);

      api.setData({});
      api.setData({ ...api.getRecord(), dtInicio: record.dtInicio, dtFim: record.dtFim });
    }
  }

  @OnMount(Aliases.FILTER_BENEFICIARY_CHANGE)
  onFilterMount(event: MountEvent): void {

    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    void HttpService.get(Endpoint.GET.DEFAULT_FILTER_VALUES_ALT_BENEF, { nrSeqContrato }).then(response => {
      const value = response.body as DefaultFilterValues;
      const params = {
        vlDominioData: value.vlDominioData,
        cdSituacao: value.cdSituacao,
        vlDominio: value.vlDominio,
      };
      FilterService.restoreFilterState(event, params);
    });
  }

  @OnUnmount(Aliases.FILTER_BENEFICIARY_CHANGE)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnClick(Aliases.ACTION_PRINT)
  onClickPrint(): void {
    const dadosFiltro = this.filter.getRecord() as DefaultFilterValues;
    const { dtInicio, dtFim } = dadosFiltro;
    const value = dadosFiltro as unknown as DefaultFilterValues;
    const situacaoTrabalhista = value.cdSituacao as SituacaoTrabalhista;
    const { cdSituacao } = situacaoTrabalhista;
    const tipoData = value.vlDominioData as TipoData;
    const { vlDominioData } = tipoData;
    const situacaoBeneficiario = value.vlDominio as SituacaoBeneficiario;
    const segurado = value.nrSeqSegurado as unknown as Segurado;
    const subestipulanteFiltro = value.nrSeqSubestipulante as unknown as Subestipulante;
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.reportService = new ReportService('15', Endpoint.GET.RELATORIOS_POR_GRUPO);
    const { nrSeqSubestipulante } = StipulatorContext.getCurrentContract();
    void this.reportService.getRelatoriosByCdGrupo()
      .then(() => {
        this.reportService.abreListaRelatorios({
          NR_SEQ_CONTRATO: nrSeqContrato,
          NR_SEQ_SEGURADO: segurado ? segurado.nrSeqSegurado : undefined,
          NR_SEQ_SUBESTIPULANTE: nrSeqSubestipulante ? nrSeqSubestipulante : subestipulanteFiltro ? subestipulanteFiltro.nrSeqSubestipulante : undefined,
          IE_SITUACAO: situacaoBeneficiario ? situacaoBeneficiario.vlDominio : undefined,
          IE_TRABALHISTA: cdSituacao,
          IE_TIPO_DATA: vlDominioData,
          DT_INICIO: inDatepickerToTimestamp(dtInicio as unknown as string),
          DT_FIM: inDatepickerToTimestamp(dtFim as unknown as string),
        });
      });

  }

  private onDadosPessoais(nrSeqBeneficiario: number, cdPessoaFisica: number): void {
    const { nrSeqGrupoContratoWeb, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.historyService.push(Route.BENEFICIARY_DATA, { nrSeqBeneficiario, cdPessoaFisica, nrSeqGrupoContratoWeb, nrSeqContrato });
  }

  private onAlterarProduto(nrSeqBeneficiario: number, cdPessoaFisica: number): void {
    const { nrSeqGrupoContratoWeb, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.historyService.push(Route.ALTERAR_PRODUTO, { nrSeqGrupoContratoWeb, nrSeqContrato, nrSeqBeneficiario, cdPessoaFisica });
  }

  private onSolicitarRepasse(nrSeqBeneficiario: number): void {
    const { nrSeqGrupoContratoWeb, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.historyService.push(Route.SOLICITAR_REPASSE, { nrSeqGrupoContratoWeb, nrSeqContrato, nrSeqBeneficiario });
  }

  private onFinalizarRepasse(nrSeqBeneficiario: number): void {
    const { nrSeqGrupoContratoWeb, nrSeqContrato } = this.historyService.getCurrentRouteParams();
    this.historyService.push(Route.FINALIZAR_REPASSE, { nrSeqGrupoContratoWeb, nrSeqContrato, nrSeqBeneficiario });
  }

  private onSegundaViaCartao(nrSeqBeneficiario: number): void {
    this.historyService.push(Route.CARTAO_SOLICITAR, { nrSeqBeneficiario });
  }

  private onAlterarMatricula(nrSeqBeneficiario: number): void {
    this.historyService.push(Route.ALTERAR_MATRICULA, { nrSeqBeneficiario });
  }

  private onMigrarContrato(nrSeqBeneficiario: number): void {
    this.historyService.push(Route.MIGRAR_CONTRATO, { nrSeqBeneficiario });
  }
  private onAlterarLocalizacao(nrSeqBeneficiario: number): void {
    this.historyService.push(Route.ALTERAR_LOCALIZACAO, { nrSeqBeneficiario });
  }
  private onAlterarDataAdmissao(nrSeqBeneficiario: number): void {
    this.historyService.push(Route.ALTERAR_ADMISSAO, { nrSeqBeneficiario });
  }

  private onTransferirTitularidade(nrSeqBeneficiario: number): void {
    this.historyService.push(Route.TRANSFERIR_TITULARIDADE, { nrSeqBeneficiario });
  }

  private onAlterarVinculo(nrSeqBeneficiario: number): void {
    this.historyService.push(Route.ALTERAR_VINCULO, { nrSeqBeneficiario });
  }

  private onAlterarSubestipulante(nrSeqBeneficiario: number): void {
    this.historyService.push(Route.ALTERAR_SUBESTIPULANTE, { nrSeqBeneficiario });
  }

  private renderDataAdmissao(dados: Data): boolean {
    if (StipulatorContext.isAllowedTwsFunction('9126_2')) {
      return dados != null && dados.nrSeqTitular == null;
    }

    return false;
  }

  private renderLocalizacao(): boolean {
    return StipulatorContext.isAllowedTwsFunction('9126_4');
  }

  private renderMatricula(): boolean {
    return StipulatorContext.isAllowedTwsFunction('9126_5');
  }

  private renderProduto(dados: Data): boolean {
    if (StipulatorContext.isAllowedTwsFunction('9126_6')) {
      return true;
    }

    if (StipulatorContext.isAllowedTwsFunction('9126_6', Entry.T)) {
      return dados != null && dados.nrSeqTitular == null;
    }

    return false;
  }

  private renderVinculo(): boolean {
    return StipulatorContext.isAllowedTwsFunction('9126_7');
  }

  private renderCompartilhamentoRiscoFim(dados: Data): boolean {
    if (StipulatorContext.isAllowedTwsFunction('9126_10')) {
      return dados != null && dados.nrSequenciaRepasse != null;
    }

    return false;
  }

  private renderCompartilhamentoRiscoSolic(dados: Data): boolean {
    if (StipulatorContext.isAllowedTwsFunction('9126_10')) {
      return dados != null && dados.nrSequenciaRepasse == null;
    }

    return false;
  }

  private renderMigrarContrato(dados: Data): boolean {
    if (StipulatorContext.isAllowedTwsFunction('9126_8')) {
      return dados != null && dados.nrSeqTitular == null && dados.tipoContrato == 'C';
    }

    return false;
  }

  private renderSegundavia(): boolean {
    return StipulatorContext.isAllowedTwsFunction('9126_11');
  }

  private renderAlterarTitularidade(dados: Data): boolean {
    if (StipulatorContext.isAllowedTwsFunction('9126_12')) {
      return dados != null && dados.nrSeqTitular == null
        && dados.tipoContrato == 'C' && dados.qtdDependentes > 0;
    }

    return false;
  }

  private renderAlterarSubestipulante(dados: Data): boolean {
    if (StipulatorContext.isAllowedTwsFunction('9126_17')) {
      return StipulatorContext.getCurrentContract().nrSeqSubestipulante == null
        && dados.tipoContrato == 'C' && StipulatorContext.getCurrentContract().qtdSubestipulantes > 0;
    }

    return false;
  }

}
export default AlteracaoBeneficiarioController;
